import React, { useEffect, useState } from "react";
import api from "../../utils/axiosConfig";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import ThreeModelViewer from "../ThreeModelViewer";
import {
  FaHeart,
  FaTrash,
  FaRegHeart,
  FaSearchPlus,
  FaGrid3,
  FaList,
  FaThLarge,
  FaEye,
} from "react-icons/fa";
import { RiLoader4Line } from "react-icons/ri";
import {
  HiOutlineViewGrid,
  HiOutlineViewList,
  HiOutlineViewBoards,
} from "react-icons/hi";

// Configuration
const fallbackImageUrl =
  "https://via.placeholder.com/500x500/ffffff/cccccc?text=Premium+Product";


// Styles Liquid Glass
const FavoritesPage = styled.div`
  margin: 3rem auto;
  padding: 0 2rem;
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f5f0 0%, #f0ebe0 100%);
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2.5rem;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.1),
      rgba(255, 255, 255, 0.05)
    );
    pointer-events: none;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    padding: 1.5rem;
    text-align: center;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    margin-bottom: 2rem;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #2c1810;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 15px;
  font-family: "Cormorant Garamond", serif;
  letter-spacing: 1px;
  position: relative;
  z-index: 2;

  svg {
    color: #d4af37;
    font-size: 2rem;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
    gap: 12px;

    svg {
      font-size: 1.5rem;
    }
  }

  @media (max-width: 480px) {
    font-size: 1.8rem;
    gap: 10px;

    svg {
      font-size: 1.3rem;
    }
  }
`;

const ViewControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    gap: 8px;
  }

  @media (max-width: 480px) {
    gap: 6px;
    flex-wrap: wrap;
    justify-content: center;
  }
`;

const ViewButton = styled.button`
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: ${(props) => (props.active ? "#d4af37" : "#2c1810")};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: "Cormorant Garamond", serif;
  font-weight: 500;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-1px);
  }

  &.active {
    background: rgba(212, 175, 55, 0.1);
    border-color: rgba(212, 175, 55, 0.3);
  }

  @media (max-width: 768px) {
    padding: 6px 10px;
    font-size: 0.8rem;
    gap: 4px;
  }

  @media (max-width: 480px) {
    padding: 5px 8px;
    font-size: 0.75rem;
    gap: 3px;
  }
`;

const FavoritesCount = styled.span`
  font-size: 1.1rem;
  color: #8b5a2b;
  font-weight: 500;
  font-family: "Cormorant Garamond", serif;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

// Grille responsive avec liquid glass
const GridContainer = styled.div`
  display: grid;
  grid-template-columns: ${(props) => {
    switch (props.viewType) {
      case "list":
        return "1fr";
      case "cards":
        return "repeat(auto-fill, minmax(250px, 1fr))";
      default:
        return "repeat(auto-fill, minmax(300px, 1fr))";
    }
  }};
  gap: 20px;
  margin-top: 2rem;
`;

const ProductCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  position: relative;
  cursor: pointer;
  display: ${(props) => (props.viewType === "list" ? "flex" : "block")};
  gap: ${(props) => (props.viewType === "list" ? "15px" : "0")};
  height: ${(props) => (props.viewType === "list" ? "10rem" : "auto")};

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
    border-color: rgba(212, 175, 55, 0.3);
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.1),
      rgba(255, 255, 255, 0.05)
    );
    pointer-events: none;
    z-index: 1;
  }
`;

const ImageContainer = styled.div`
  width: ${(props) => (props.viewType === "list" ? "150px" : "100%")};
  height: ${(props) => (props.viewType === "list" ? "100%" : "220px")};
  position: relative;
  overflow: hidden;
  background: rgba(249, 249, 249, 0.1);
  flex-shrink: 0;
  z-index: 2;
`;

const Badge = styled.span`
  position: absolute;
  top: 15px;
  right: 15px;
  background: linear-gradient(135deg, #d4af37, #af8f4c);
  color: white;
  padding: 8px 15px;
  border-radius: 25px;
  font-size: 0.8rem;
  font-weight: 600;
  z-index: 3;
  box-shadow: 0 4px 15px rgba(212, 175, 55, 0.3);
  font-family: "Cormorant Garamond", serif;
`;

const QuickView = styled.div`
  position: absolute;
  bottom: ${(props) => (props.viewType === "list" ? "0" : "-50px")};
  left: 0;
  width: 100%;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  color: white;
  padding: 15px;
  text-align: center;
  transition: all 0.3s ease;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  z-index: 3;
  font-family: "Cormorant Garamond", serif;
  font-weight: 500;

  ${ProductCard}:hover & {
    bottom: 0;
  }
`;

const ProductContent = styled.div`
  padding: ${(props) => (props.viewType === "list" ? "0.8rem 0" : "1.2rem")};
  flex: ${(props) => (props.viewType === "list" ? "1" : "none")};
  z-index: 2;
`;

const ProductTitle = styled.h3`
  font-size: ${(props) => (props.viewType === "list" ? "1rem" : "1rem")};
  margin: 0 0 0.4rem 0;
  color: #2c1810;
  font-weight: 600;
  font-family: "Cormorant Garamond", serif;
  letter-spacing: 0.3px;
  ${(props) =>
    props.viewType !== "list" &&
    `
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `}
`;
const PriceContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const CurrentPrice = styled.span`
  font-size: ${(props) => (props.viewType === "list" ? "1rem" : "1.1rem")};
  font-weight: 700;
  color: ${(props) => (props.isPromo ? "#d4af37" : "#2c1810")};
  font-family: "Cormorant Garamond", serif;
`;

const OriginalPrice = styled.span`
  text-decoration: line-through;
  color: #999;
  font-size: 0.9em;
  margin-left: 8px;
  font-family: "Cormorant Garamond", serif;
`;

const ProductMeta = styled.div`
  font-size: ${(props) => (props.viewType === "list" ? "0.8rem" : "0.9rem")};
  color: #8b5a2b;
  margin-bottom: ${(props) =>
    props.viewType === "list" ? "0.4rem" : "0.8rem"};
  display: flex;
  gap: 12px;
  align-items: center;
  font-family: "Cormorant Garamond", serif;
`;

const ColorIndicator = styled.span`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: inline-block;
  border: 2px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${(props) => (props.viewType === "list" ? "0.8rem" : "1.5rem")};
  gap: 10px;
`;

const ActionButton = styled.button`
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  font-size: 0.8rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
  color: #373737;
  font-family: "Cormorant Garamond", serif;
  font-weight: 500;

  &:hover {
    background: rgba(55, 55, 55, 0.1);
    border-color: rgba(55, 55, 55, 0.3);
    transform: translateY(-1px);
  }

  &.view-button {
    color: #373737;

    &:hover {
      background: rgba(55, 55, 55, 0.1);
      border-color: rgba(55, 55, 55, 0.3);
    }
  }
`;

const EmptyState = styled(motion.div)`
  text-align: center;
  padding: 4rem 2rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  grid-column: 1 / -1;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.1),
      rgba(255, 255, 255, 0.05)
    );
    pointer-events: none;
  }
`;

const EmptyStateIcon = styled.div`
  font-size: 4rem;
  color: #d4af37;
  margin-bottom: 2rem;
  position: relative;
  z-index: 2;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 4rem;
  grid-column: 1 / -1;
`;

const ModernSpinner = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
  margin: 0 auto 2rem;

  &::before,
  &::after,
  & > div {
    content: "";
    position: absolute;
    width: 100%;
    height: 100%;
    border: 4px solid transparent;
    border-radius: 50%;
    animation: spin 2s linear infinite;
  }

  &::before {
    border-top-color: #d4af37;
    animation-delay: 0s;
  }

  &::after {
    border-right-color: #af8f4c;
    animation-delay: 0.5s;
  }

  & > div {
    border-bottom-color: #8b5a2b;
    animation-delay: 1s;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

// Helper functions
const buildImageUrl = (filename) => {
  if (!filename || typeof filename !== "string") return "";

  // URL complète détectée, on la retourne brute
  if (/^https?:\/\//i.test(filename)) {
    return filename;
  }

  // Sinon on construit URL locale
  let cleanFilename = filename.startsWith("/") ? filename.slice(1) : filename;

  if (cleanFilename.startsWith("public/")) {
    cleanFilename = cleanFilename.slice("public/".length);
  }

  if (!cleanFilename.startsWith("uploads/")) {
    cleanFilename = `uploads/${cleanFilename}`;
  }

  return `${process.env.REACT_APP_API_URL || "http://localhost:5000"}/${cleanFilename}`.replace(/([^:]\/)\/+/g, "$1");
};
const normalize = (str) => (str ? str.trim().toLowerCase() : "");

const getVariantByColorAndTaille = (product, couleur, taille) => {
  if (!product?.variantes) return null;
  return product.variantes.find(
    (v) =>
      normalize(v.couleur) === normalize(couleur) &&
      v.stock?.some((s) => normalize(s.taille) === normalize(taille))
  );
};

export default function MesFavoris() {
  const [favoris, setFavoris] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [viewType, setViewType] = useState("cards"); 
  const navigate = useNavigate();

  useEffect(() => {
    const fetchFavoris = async () => {
      try {
        const res = await api.get(`/api/favorites`);
        // Filtrer les favoris invalides (sans produit)
        const validFavoris = (res.data.favorites || []).filter(fav => fav.product);
        setFavoris(validFavoris);
      } catch (error) {
        console.error("Erreur lors de la récupération des favoris :", error);
      } finally {
        setLoading(false);
      }
    };

    fetchFavoris();
  }, []);

  const handleRemoveFavorite = async (productId, e) => {
    e.stopPropagation();
    setProcessing(`remove-${productId}`);

    // Mise à jour optimiste immédiate (avant la réponse du serveur)
    const updatedFavoris = favoris.filter((fav) => fav.product && fav.product._id !== productId);
    setFavoris(updatedFavoris);

    try {
      await api.delete(`/api/favorites/${productId}`);
      // Si succès, l'état est déjà mis à jour
    } catch (error) {
      console.error("Erreur lors de la suppression du favori :", error);
      // En cas d'erreur, remettre l'ancien état
      setFavoris(favoris);
      // Optionnel : afficher un message d'erreur
    } finally {
      setProcessing(null);
    }
  };

  const handleQuickView = (productId, e) => {
    e.stopPropagation();
    navigate(`/product/${productId}?quickview=true`);
  };

  if (loading) {
    return (
      <FavoritesPage>
        <LoadingState>
          <ModernSpinner>
            <div></div>
          </ModernSpinner>
          <p>Chargement de vos favoris...</p>
        </LoadingState>
      </FavoritesPage>
    );
  }

  return (
    <FavoritesPage>
      <div style={{ height: window.innerWidth <= 768 ? "4vh" : "15vh" }}></div>
      <PageHeader>
        <Title>
          <FaHeart />
          Mes Favoris
        </Title>
        <ViewControls>
         
          <ViewButton
            active={viewType === "cards"}
            onClick={() => setViewType("cards")}
          >
            <HiOutlineViewBoards />
            Cartes
          </ViewButton>
          <ViewButton
            active={viewType === "list"}
            onClick={() => setViewType("list")}
          >
            <HiOutlineViewList />
            Liste
          </ViewButton>
        </ViewControls>
        <FavoritesCount>
          {favoris.length} article{favoris.length !== 1 ? "s" : ""}
        </FavoritesCount>
      </PageHeader>

      <GridContainer viewType={viewType}>
        <AnimatePresence>
          {favoris.length === 0 ? (
            <EmptyState
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              <EmptyStateIcon>
                <FaRegHeart />
              </EmptyStateIcon>
              <h3>Votre liste de favoris est vide</h3>
              <p>
                Ajoutez des articles à vos favoris pour les retrouver facilement
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{
                  marginTop: "1.5rem",
                  padding: "0.8rem 2rem",
                  background: "#3498db",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontWeight: "600",
                }}
                onClick={() => navigate("/products/femme")}
              >
                Découvrir nos produits
              </motion.button>
            </EmptyState>
          ) : (
            favoris.map((fav, i) => {
              const { product, couleur = "", taille = "" } = fav;
              if (!product) return null;

              const variante =
                getVariantByColorAndTaille(product, couleur, taille) ||
                product.variantes?.[0];

              const firstImage = variante?.images?.[0];
              const is3D = firstImage?.match(/\.(glb|gltf|obj)$/i);
              const imageUrl =
                firstImage && typeof firstImage === "string"
                  ? buildImageUrl(firstImage)
                  : fallbackImageUrl;

              // Clé unique basée sur l'ID du produit (cohérent avec la suppression)
              const key = `favorite-${product._id}`;

              return (
                <ProductCard key={key} viewType={viewType}>
                  <Badge>Favori</Badge>

                  <ImageContainer viewType={viewType}>
                    {firstImage ? (
                      is3D ? (
                        <ThreeModelViewer
                          url={imageUrl}
                          width="100%"
                          height="100%"
                        />
                      ) : (
                        <img
                          src={imageUrl}
                          alt={product.name || "Image produit"}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                          onError={(e) => {
                            e.currentTarget.src = fallbackImageUrl;
                          }}
                        />
                      )
                    ) : (
                      <img
                        src={fallbackImageUrl}
                        alt="Pas d'image"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    )}

                    <QuickView
                      viewType={viewType}
                      onClick={(e) => handleQuickView(product._id, e)}
                    >
                      <FaSearchPlus /> Voir rapidement
                    </QuickView>
                  </ImageContainer>

                  <ProductContent viewType={viewType}>
                    <ProductTitle
                      viewType={viewType}
                      title={`${product.name} - ${
                        product.price.promoPrice
                          ? `${product.price.promoPrice.toFixed(2)} MAD (Promo)`
                          : `${product.price.originalPrice.toFixed(2)} MAD`
                      }`}
                    />

                    <PriceContainer>
                      {product.price.promoPrice ? (
                        <>
                          <CurrentPrice viewType={viewType} isPromo>
                            {product.price.promoPrice.toFixed(2)} MAD
                          </CurrentPrice>
                          <OriginalPrice>
                            {product.price.originalPrice.toFixed(2)} MAD
                          </OriginalPrice>
                        </>
                      ) : (
                        <CurrentPrice viewType={viewType}>
                          {product.price.originalPrice.toFixed(2)} MAD
                        </CurrentPrice>
                      )}
                    </PriceContainer>

                    <ProductMeta viewType={viewType}>
                      <span>Type: {product.type}</span>
                    </ProductMeta>

                    {variante?.couleur && (
                      <ProductMeta viewType={viewType}>
                        <span>Couleur: {variante.couleur}</span>
                        <ColorIndicator
                          style={{
                            backgroundColor: variante.couleur.toLowerCase(),
                          }}
                        />
                      </ProductMeta>
                    )}

                    {taille && (
                      <ProductMeta viewType={viewType}>
                        Taille: {taille}
                      </ProductMeta>
                    )}

                    <ActionBar viewType={viewType}>
                      <ActionButton
                        className="view-button"
                        onClick={(e) => handleQuickView(product._id, e)}
                      >
                        <FaEye />
                        Voir
                      </ActionButton>
                      <ActionButton
                        onClick={(e) => handleRemoveFavorite(product._id, e)}
                        disabled={processing === `remove-${product._id}`}
                      >
                        {processing === `remove-${product._id}` ? (
                          <RiLoader4Line className="spin" />
                        ) : (
                          <FaTrash />
                        )}
                        Supprimer
                      </ActionButton>
                    </ActionBar>
                  </ProductContent>
                </ProductCard>
              );
            })
          )}
        </AnimatePresence>
      </GridContainer>
    </FavoritesPage>
  );
}
